import { useEffect, useState } from "react";
import axios from "axios";
import { GroupChannel, Member, User } from "@sendbird/chat/lib/__definition";
import { useSendbirdStateContext } from "@sendbird/uikit-react";

import { endpoints } from "../../../api/endpoints";
import { getUserFromConversation } from "./getUserFromConversation";

type RespondentProfile = {
  respondent: Member | null;
  displayName: string;
  avatarUrl: string;
};

function channelNeedsMemberRefresh(channel: GroupChannel): boolean {
  return !channel.members || channel.members.length === 0;
}

export function useConversationRespondent(
  channel: GroupChannel | null,
  isGroupChat: boolean
): RespondentProfile {
  const {
    stores: {
      sdkStore: { sdk },
      userStore,
    },
  } = useSendbirdStateContext();

  const [respondent, setRespondent] = useState<Member | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  useEffect(() => {
    if (!channel || isGroupChat) {
      setRespondent(null);
      return;
    }

    let cancelled = false;
    const currentUser = userStore.user as User;

    const resolveRespondent = async () => {
      let resolved = getUserFromConversation(channel, currentUser);
      const shouldRefreshMembers = channelNeedsMemberRefresh(channel);

      if (shouldRefreshMembers && sdk?.groupChannel && channel.url) {
        try {
          const freshChannel = await sdk.groupChannel.getChannel(channel.url);
          if (!cancelled) {
            const fromMembers = getUserFromConversation(freshChannel, currentUser);
            if (fromMembers) {
              resolved = fromMembers;
            }
          }
        } catch {
          // Fall back to list-item channel + channel-name parsing.
        }
      }

      if (!cancelled) {
        setRespondent(resolved);
      }
    };

    resolveRespondent().catch(() => {
      if (!cancelled) {
        setRespondent(getUserFromConversation(channel, currentUser));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [channel, isGroupChat, sdk, userStore.user]);

  useEffect(() => {
    if (!respondent?.userId || isGroupChat) {
      setDisplayName("");
      setAvatarUrl("");
      return;
    }

    let cancelled = false;

    axios
      .get(`${endpoints.getPublicProfile}${respondent.userId}`)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const profile = response.data as {
          name?: string;
          last_name?: string;
          avatar_url?: string;
        };
        const fullName = `${profile.name || ""} ${profile.last_name || ""}`.trim();
        setDisplayName(fullName);
        setAvatarUrl(profile.avatar_url || "");
      })
      .catch(() => {
        if (!cancelled) {
          setDisplayName("");
          setAvatarUrl("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [respondent?.userId, isGroupChat]);

  return { respondent, displayName, avatarUrl };
}
